class Tabs {
  constructor(container) {
    this.container = container;
    this.tabs = container.querySelectorAll(".tabs__nav-item");
    this.contents = container.querySelectorAll(".tabs__content");
    this.mediaRows = container.querySelectorAll(".tabs-media__row");
    this.mq = window.matchMedia("(min-width: 680px)");
    this.activeTabId = null;
    this.init();
  }

  init() {
    this.container.setAttribute("role", "tablist");

    this.tabs.forEach((tab, index) => {
      const tabId = tab.dataset.tabId;
      const panel = this.container.querySelector(
        `[data-content-id="${tabId}"]`
      );

      tab.setAttribute("role", "tab");
      tab.setAttribute("id", `tab-${tabId}`);
      tab.setAttribute("aria-controls", `panel-${tabId}`);
      tab.setAttribute("tabindex", "-1");

      if (panel) {
        panel.setAttribute("role", "tabpanel");
        panel.setAttribute("id", `panel-${tabId}`);
        panel.setAttribute("aria-labelledby", `tab-${tabId}`);
        panel.setAttribute("hidden", "");
      }

      tab.removeEventListener("click", this.handleTabClick);
      tab.removeEventListener("keydown", this.handleTabKeyDown);
      tab.addEventListener("click", this.handleTabClick.bind(this));
      tab.addEventListener("keydown", this.handleTabKeyDown.bind(this));
    });

    const activeTab =
      this.container.querySelector(".tabs__nav-item--active") || this.tabs[0];
    if (activeTab) {
      this.activeTabId = activeTab.dataset.tabId;
      this.setInitialTab(this.activeTabId);
    }

    this.observeDOM();

    this.mq.addEventListener("change", this.handleMediaQueryChange.bind(this));

    this.initLearnMore();
  }

  handleTabClick(event) {
    this.activateTab(event.currentTarget.dataset.tabId);
  }

  handleTabKeyDown(e) {
    const tab = e.currentTarget;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      this.activateTab(tab.dataset.tabId);
    }
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      const tabsArray = Array.from(this.tabs);
      const currentIndex = tabsArray.indexOf(tab);
      let newIndex =
        e.key === "ArrowRight" ? currentIndex + 1 : currentIndex - 1;

      if (newIndex < 0) newIndex = tabsArray.length - 1;
      if (newIndex >= tabsArray.length) newIndex = 0;

      tabsArray[newIndex].focus();
      this.activateTab(tabsArray[newIndex].dataset.tabId);
    }
  }

  handleMediaQueryChange() {
    if (this.activeTabId) {
      this.activateTab(this.activeTabId);
    }
  }

  observeDOM() {
    const observer = new MutationObserver((mutations) => {
      let mediaRowsUpdated = false;
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          const newMediaRows =
            this.container.querySelectorAll(".tabs-media__row");
          if (newMediaRows.length !== this.mediaRows.length) {
            mediaRowsUpdated = true;
            this.mediaRows = newMediaRows;
          }
        }
      });
      if (mediaRowsUpdated && this.activeTabId) {
        this.activateTab(this.activeTabId);
      }
    });

    observer.observe(this.container, {
      childList: true,
      subtree: true,
    });
  }

  setInitialTab(tabId) {
    this.tabs.forEach((tab) => {
      tab.classList.remove("tabs__nav-item--active");
      tab.setAttribute("aria-selected", "false");
      tab.setAttribute("tabindex", "-1");
    });

    this.contents.forEach((content) => {
      content.classList.remove("tabs__content--active");
      content.setAttribute("hidden", "");
    });

    this.mediaRows.forEach((media) =>
      media.classList.remove("tabs-media__row--active")
    );

    const activeTab = this.container.querySelector(`[data-tab-id="${tabId}"]`);
    if (activeTab) {
      activeTab.classList.add("tabs__nav-item--active");
      activeTab.setAttribute("aria-selected", "true");
      activeTab.setAttribute("tabindex", "0");
      this.activeTabId = tabId;
    }

    const activeContent = this.container.querySelector(
      `[data-content-id="${tabId}"]`
    );
    if (activeContent) {
      activeContent.classList.add("tabs__content--active");
      activeContent.removeAttribute("hidden");
    }

    let activeMedia = null;
    if (this.mq.matches) {
      activeMedia = this.container.querySelector(
        `.tabs-media__row--desktop[data-media-id="${tabId}"]`
      );
    } else {
      activeMedia =
        this.container.querySelector(
          `.tabs-media__row--mobile[data-media-id="${tabId}"]`
        ) ||
        this.container.querySelector(
          `.tabs-media__row--desktop[data-media-id="${tabId}"]`
        );
    }

    if (activeMedia) {
      activeMedia.classList.add("tabs-media__row--active");
      this.scrollMainColumnIntoView(activeMedia);
    }
  }

  activateTab(tabId) {
    this.tabs.forEach((tab) => {
      tab.classList.remove("tabs__nav-item--active");
      tab.setAttribute("aria-selected", "false");
      tab.setAttribute("tabindex", "-1");
    });

    this.contents.forEach((content) => {
      content.classList.remove("tabs__content--active");
      content.setAttribute("hidden", "");
    });

    this.mediaRows.forEach((media) =>
      media.classList.remove("tabs-media__row--active")
    );

    const activeTab = this.container.querySelector(`[data-tab-id="${tabId}"]`);
    if (activeTab) {
      activeTab.classList.add("tabs__nav-item--active");
      activeTab.setAttribute("aria-selected", "true");
      activeTab.setAttribute("tabindex", "0");
      activeTab.focus();
      this.activeTabId = tabId;
    }

    const activeContent = this.container.querySelector(
      `[data-content-id="${tabId}"]`
    );
    if (activeContent) {
      activeContent.classList.add("tabs__content--active");
      activeContent.removeAttribute("hidden");
    }

    let activeMedia = null;
    if (this.mq.matches) {
      activeMedia = this.container.querySelector(
        `.tabs-media__row--desktop[data-media-id="${tabId}"]`
      );
    } else {
      activeMedia =
        this.container.querySelector(
          `.tabs-media__row--mobile[data-media-id="${tabId}"]`
        ) ||
        this.container.querySelector(
          `.tabs-media__row--desktop[data-media-id="${tabId}"]`
        );
    }

    if (activeMedia) {
      activeMedia.classList.add("tabs-media__row--active");
      this.scrollMainColumnIntoView(activeMedia);
    }

    if (this.checkOverflow) this.checkOverflow();
  }

  scrollMainColumnIntoView(mediaRow) {
    const mainColumn = mediaRow.querySelector('[data-column-type="main"]');

    if (mainColumn && mediaRow) {
      const scrollLeft =
        mainColumn.offsetLeft -
        mediaRow.clientWidth / 2 +
        mainColumn.clientWidth / 2;

      mediaRow.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });
    }
  }

  initLearnMore() {
    const checkOverflow = () => {
      this.container
        .querySelectorAll(".tabs__panel-wrapper")
        .forEach((wrapper) => {
          const truncated = wrapper.querySelector(".tabs__panel--truncated");
          const full = wrapper.querySelector(".tabs__panel--full");
          const button = wrapper.querySelector(".learn-more-btn");
          if (!truncated || !full || !button) return;

          if (!this.mq.matches) {
            truncated.style.display = "none";
            full.style.display = "block";
            button.style.display = "none";
            wrapper.classList.remove("expanded");
            return;
          }

          truncated.style.display = "block";
          full.style.display = "none";
          wrapper.classList.remove("expanded");

          if (button.dataset.baseLabel) {
            button.textContent = `${button.dataset.baseLabel} More`;
          } else {
            const txt = button.textContent.trim().toLowerCase();
            button.dataset.baseLabel = txt.includes("learn")
              ? "Learn"
              : txt.includes("view")
              ? "View"
              : "Show";
            button.textContent = `${button.dataset.baseLabel} More`;
          }

          button.style.display = "inline-block";

          if (!button.dataset.bound) {
            button.addEventListener("click", () => {
              const base = button.dataset.baseLabel;
              const expanded = wrapper.classList.toggle("expanded");

              if (expanded) {
                truncated.style.display = "none";
                full.style.display = "block";
                button.textContent = `${base} Less`;
              } else {
                truncated.style.display = "block";
                full.style.display = "none";
                button.textContent = `${base} More`;
              }
            });
            button.dataset.bound = "true";
          }
        });
    };

    requestAnimationFrame(checkOverflow);

    window.addEventListener("resize", checkOverflow);
    this.mq.addEventListener("change", checkOverflow);

    this.tabs.forEach((tab) => {
      tab.addEventListener("click", checkOverflow);
    });

    this.checkOverflow = checkOverflow;
  }
}

document.querySelectorAll(".tabs").forEach((container) => {
  new Tabs(container);
});
